/**
 * Logger Utility - Dev Environment & Designated Developers Only
 *
 * Usage:
 *   import devLogger from 'helpers/logger';
 *   devLogger.log('[TAG]', 'message', data);
 *   devLogger.warn('[TAG]', 'warning message');
 *   devLogger.error('[TAG]', 'error message');
 *
 * Only logs in:
 * 1. Development environment (ENV = 'dev') - ALL USERS
 * 2. OR when user is a designated developer (from settings.developers array in login API)
 *
 * Backend Setup:
 * - Add 'developers' array to settings table: ['dev@example.com', 'admin@example.com']
 * - Login API should return: { isDeveloper: true/false, ...otherUserData }
 * - isDeveloper = true if user.email is in settings.developers array
 */

import { ENV } from 'config/env';
import secureLocalStorage from 'react-secure-storage';

// Check if logging is enabled
const isLoggingEnabled = () => {
  // Always allow in dev environment
  if (ENV === 'dev') {
    return true;
  }

  // In production, check if user is a designated developer
  try {
    // Check secure storage for isDeveloper flag (set by login API)
    const isDeveloper = secureLocalStorage.getItem('isDeveloper');
    return isDeveloper === true || isDeveloper === 'true';
  } catch (error) {
    // If can't access storage, don't log in production
    return false;
  }
};

// Color codes for console
const COLORS = {
  reset: '\x1b[0m',
  bright: '\x1b[1m',
  dim: '\x1b[2m',

  // Foreground colors
  red: '\x1b[31m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  blue: '\x1b[34m',
  magenta: '\x1b[35m',
  cyan: '\x1b[36m',
  white: '\x1b[37m',

  // Background colors
  bgRed: '\x1b[41m',
  bgGreen: '\x1b[42m',
  bgYellow: '\x1b[43m',
  bgBlue: '\x1b[44m',
};

/**
 * Smart JSON handler
 * - If data is already stringified JSON (is a string), parse and re-stringify
 * - If data is an object, return as-is for console.log to handle
 * - If data is null/undefined, return null
 */
const smartJSONHandler = (data) => {
  // Handle null and undefined
  if (data === null || data === undefined) {
    return null;
  }

  // If it's already a string, check if it's JSON
  if (typeof data === 'string') {
    try {
      // Try to parse it - if it succeeds, it's JSON
      const parsed = JSON.parse(data);
      // Return the parsed object so console can display it nicely
      return parsed;
    } catch (e) {
      // Not JSON, just return the string as-is
      return data;
    }
  }

  // If it's an object, array, number, boolean, etc., return as-is
  return data;
};

/**
 * Format log message with color and timestamp
 */
const formatMessage = (level, tag) => {
  const timestamp = new Date().toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

  let levelColor = COLORS.white;
  let levelSymbol = '📝';

  switch (level) {
    case 'log':
      levelColor = COLORS.blue;
      levelSymbol = '📝';
      break;
    case 'warn':
      levelColor = COLORS.yellow;
      levelSymbol = '⚠️';
      break;
    case 'error':
      levelColor = COLORS.red;
      levelSymbol = '❌';
      break;
    case 'success':
      levelColor = COLORS.green;
      levelSymbol = '✅';
      break;
    case 'info':
      levelColor = COLORS.cyan;
      levelSymbol = 'ℹ️';
      break;
    default:
      levelColor = COLORS.white;
      levelSymbol = '•';
  }

  return `${levelColor}${levelSymbol} [${timestamp}]${COLORS.reset} ${tag}`;
};

/**
 * Dev Logger Object
 *
 * Simplified API: devLogger.log(anchor, data)
 * - anchor: Tag/identifier string like '[ESCALATION]', '[API]', etc.
 * - data: Object, array, string, or stringified JSON
 *         Automatically handles stringified JSON by parsing it
 */
const devLogger = {
  /**
   * Basic log (blue)
   * devLogger.log('[COMPONENT]', { id: 123, name: 'John' });
   * devLogger.log('[COMPONENT]', '{"id":123,"name":"John"}'); // Already stringified
   * devLogger.log('[COMPONENT]', 'Simple message');
   */
  log: (anchor = '', data = null) => {
    if (!isLoggingEnabled()) return;

    const formattedMsg = formatMessage('log', anchor);
    const processedData = smartJSONHandler(data);

    if (processedData !== null && processedData !== undefined) {
      console.log(formattedMsg, processedData);
    } else {
      console.log(formattedMsg);
    }
  },

  /**
   * Warning log (yellow)
   * devLogger.warn('[VALIDATION]', { field: 'email', reason: 'Invalid' });
   */
  warn: (anchor = '', data = null) => {
    if (!isLoggingEnabled()) return;

    const formattedMsg = formatMessage('warn', anchor);
    const processedData = smartJSONHandler(data);

    if (processedData !== null && processedData !== undefined) {
      console.warn(formattedMsg, processedData);
    } else {
      console.warn(formattedMsg);
    }
  },

  /**
   * Error log (red)
   * devLogger.error('[API]', error);
   * devLogger.error('[API]', { message: 'Network error', code: 500 });
   */
  error: (anchor = '', data = null) => {
    if (!isLoggingEnabled()) return;

    const formattedMsg = formatMessage('error', anchor);
    const processedData = smartJSONHandler(data);

    if (processedData !== null && processedData !== undefined) {
      console.error(formattedMsg, processedData);
    } else {
      console.error(formattedMsg);
    }
  },

  /**
   * Success log (green)
   * devLogger.success('[API]', { ticketId: '123', status: 'created' });
   */
  success: (anchor = '', data = null) => {
    if (!isLoggingEnabled()) return;

    const formattedMsg = formatMessage('success', anchor);
    const processedData = smartJSONHandler(data);

    if (processedData !== null && processedData !== undefined) {
      console.log(formattedMsg, processedData);
    } else {
      console.log(formattedMsg);
    }
  },

  /**
   * Info log (cyan)
   * devLogger.info('[SETUP]', { version: '1.0', user: 'john' });
   */
  info: (anchor = '', data = null) => {
    if (!isLoggingEnabled()) return;

    const formattedMsg = formatMessage('info', anchor);
    const processedData = smartJSONHandler(data);

    if (processedData !== null && processedData !== undefined) {
      console.log(formattedMsg, processedData);
    } else {
      console.log(formattedMsg);
    }
  },

  /**
   * Group related logs together
   * devLogger.group('[API_CALL] Fetching user data');
   * devLogger.log('[API]', 'Making request');
   * devLogger.log('[API]', 'Request completed');
   * devLogger.groupEnd();
   */
  group: (anchor = '') => {
    if (!isLoggingEnabled()) return;
    console.group(`%c${anchor}`, 'color: #0066cc; font-weight: bold;');
  },

  groupEnd: () => {
    if (!isLoggingEnabled()) return;
    console.groupEnd();
  },

  /**
   * Table log for structured data
   * devLogger.table('[DATA]', [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }]);
   * devLogger.table('[DATA]', '{"id":1,"name":"John"}'); // Stringified JSON
   */
  table: (anchor = '', data = null) => {
    if (!isLoggingEnabled()) return;

    console.log(`%c${anchor}`, 'color: #0066cc; font-weight: bold;');

    const processedData = smartJSONHandler(data);
    if (processedData !== null && processedData !== undefined) {
      console.table(processedData);
    }
  },

  /**
   * Time measurement
   * devLogger.time('[PERFORMANCE] API Call');
   * // ... code to measure
   * devLogger.timeEnd('[PERFORMANCE] API Call');
   */
  time: (anchor = '') => {
    if (!isLoggingEnabled()) return;
    console.time(`${anchor}`);
  },

  timeEnd: (anchor = '') => {
    if (!isLoggingEnabled()) return;
    console.timeEnd(`${anchor}`);
  },

  /**
   * Trace call stack
   * devLogger.trace('[TRACE]', { additionalData: 'value' });
   */
  trace: (anchor = '', data = null) => {
    if (!isLoggingEnabled()) return;

    const formattedMsg = formatMessage('info', anchor);
    const processedData = smartJSONHandler(data);

    if (processedData !== null && processedData !== undefined) {
      console.log(formattedMsg, processedData);
    } else {
      console.log(formattedMsg);
    }

    console.trace();
  },

  /**
   * Check if logging is enabled
   */
  isEnabled: () => isLoggingEnabled(),

  /**
   * Get current logging status
   */
  getStatus: () => {
    const enabled = isLoggingEnabled();
    const env = ENV;
    let email = 'N/A';
    let isDeveloper = false;

    try {
      email = secureLocalStorage.getItem('email') || 'Unknown';
      isDeveloper = secureLocalStorage.getItem('isDeveloper') === true ||
                   secureLocalStorage.getItem('isDeveloper') === 'true';
    } catch (e) {
      email = 'Error reading storage';
    }

    return {
      enabled,
      environment: env,
      userEmail: email,
      isDeveloper: isDeveloper,
      isDev: ENV === 'dev',
      reason: ENV === 'dev' ? 'Development environment' :
              (isDeveloper ? 'Designated developer' : 'Not authorized'),
    };
  },
};

export default devLogger;
